/**
 * Race Host Manager
 *
 * Service class for managing race rooms from the host's perspective.
 * Handles room creation, realtime connections, and race control.
 *
 * Refactored to:
 * - Use RaceChannelManager for channel management
 * - Use live_stats as single source of truth for race statistics
 * - Use shared utilities for calculations
 */

import { supabase } from '@/lib/supabase';
import { RaceChannelManager } from './RaceChannelManager';
import {
  calculateAccuracy,
  calculateAvgTimePerCard,
  sortLeaderboard,
  assignRanks,
} from '@/utils/raceUtils';
import type {
  RaceRoom,
  RaceParticipant,
  RaceConfig,
  StartRacePayload,
  EndRacePayload,
  LeaderboardEntry,
} from '@/types/race';

export interface RaceHostCallbacks {
  onParticipantsUpdate?: (participants: RaceParticipant[]) => void;
  onLiveStatsUpdate?: (userId: string, cardsCompleted: number, correctCount: number) => void;
  onParticipantFinish?: (participant: RaceParticipant) => void;
  onError?: (error: string) => void;
  onConnectionChange?: (connected: boolean) => void;
}

interface LiveStats {
  user_id: string;
  cards_completed: number;
  correct_count: number;
  incorrect_count: number;
  accuracy: number;
  total_time: number;
}

export class RaceHostManager {
  private userId: string;
  private raceRoomId: string | null = null;
  private room: RaceRoom | null = null;
  private channelManager: RaceChannelManager | null = null;
  private callbacks: RaceHostCallbacks;

  constructor(userId: string, callbacks: RaceHostCallbacks = {}) {
    this.userId = userId;
    this.callbacks = callbacks;
  }

  // ============================================================
  // Reconnection
  // ============================================================

  async reconnect(): Promise<{ success: boolean; room?: RaceRoom; error?: string }> {
    if (!this.raceRoomId) {
      return { success: false, error: 'No room ID' };
    }

    console.log('🔄 Reconnecting race channels...');

    try {
      // Refresh room from database
      const { data: room, error } = await supabase
        .schema('race')
        .from('rooms')
        .select('*')
        .eq('id', this.raceRoomId)
        .single();

      if (error || !room) {
        console.error('Error refreshing room:', error);
        return { success: false, error: error?.message || 'Room not found' };
      }

      this.room = room;

      // Reconnect channels
      if (this.channelManager) {
        const result = await this.channelManager.reconnect();
        if (!result.success) {
          return { success: false, error: result.error };
        }
      }

      console.log('✅ Race channels reconnected');
      return { success: true, room };
    } catch (error) {
      console.error('❌ Failed to reconnect:', error);
      return { success: false, error: (error as Error).message };
    }
  }

  // ============================================================
  // Room Management
  // ============================================================

  async resumeRoom(roomId: string): Promise<{ success: boolean; room?: RaceRoom; error?: string }> {
    try {
      const { data: room, error } = await supabase
        .schema('race')
        .from('rooms')
        .select('*')
        .eq('id', roomId)
        .eq('created_by', this.userId)
        .single();

      if (error || !room) {
        console.error('Error fetching room:', error);
        return { success: false, error: error?.message || 'Room not found or access denied' };
      }

      if (room.state === 'finished') {
        return { success: false, error: 'Cannot resume a finished race' };
      }

      this.raceRoomId = room.id;
      this.room = room;
      await this.initializeChannels();

      console.log('✅ Resumed room:', room.join_code, 'state:', room.state);
      return { success: true, room };
    } catch (error) {
      console.error('Unexpected error resuming room:', error);
      return { success: false, error: (error as Error).message };
    }
  }

  async createRoom(config: RaceConfig): Promise<{ success: boolean; room?: RaceRoom; error?: string }> {
    try {
      const joinCode = this.generateJoinCode();

      const { data: room, error } = await supabase
        .schema('race')
        .from('rooms')
        .insert({
          created_by: this.userId,
          join_code: joinCode,
          state: 'waiting',
          allowed_apps: config.allowedApps,
          max_participants: config.maxParticipants,
          question_count: config.questionCount,
        })
        .select()
        .single();

      if (error) {
        console.error('Failed to create room:', error);
        return { success: false, error: error.message };
      }

      this.raceRoomId = room.id;
      this.room = room;
      await this.initializeChannels();

      return { success: true, room };
    } catch (error) {
      console.error('Unexpected error creating room:', error);
      return { success: false, error: (error as Error).message };
    }
  }

  private generateJoinCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let code = '';
    for (let i = 0; i < 4; i++) {
      code += chars[Math.floor(Math.random() * chars.length)];
    }
    return code;
  }

  // ============================================================
  // Channel Management (delegated to RaceChannelManager)
  // ============================================================

  private async initializeChannels(): Promise<void> {
    if (!this.raceRoomId) return;

    this.channelManager = new RaceChannelManager(this.raceRoomId, this.userId, {
      onParticipantsSync: () => this.refreshParticipants(),
      onLiveStatsChange: (userId, cardsCompleted, correctCount) => {
        this.callbacks.onLiveStatsUpdate?.(userId, cardsCompleted, correctCount);
      },
      onParticipantFinish: (participant) => {
        this.callbacks.onParticipantFinish?.(participant);
      },
      onError: (error) => {
        this.callbacks.onError?.(error);
      },
      onConnectionChange: (connected) => {
        this.callbacks.onConnectionChange?.(connected);
      },
    });

    await this.channelManager.initialize();
  }

  // ============================================================
  // Race Control
  // ============================================================

  async startRace(): Promise<{ success: boolean; room?: RaceRoom; error?: string }> {
    if (!this.raceRoomId || !this.channelManager || !this.room) {
      return { success: false, error: 'No room initialized' };
    }

    try {
      const startTime = new Date().toISOString();

      const { error } = await supabase
        .schema('race')
        .from('rooms')
        .update({
          state: 'racing',
          race_start_time: startTime,
        })
        .eq('id', this.raceRoomId);

      if (error) {
        return { success: false, error: error.message };
      }

      this.room = {
        ...this.room,
        state: 'racing',
        race_start_time: startTime,
      };

      const payload: StartRacePayload = {
        startTime,
        questionCount: this.room.question_count,
      };

      await this.channelManager.broadcast('START_RACE', payload);

      console.log('Race started:', payload);
      return { success: true, room: this.room };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  }

  async endRace(reason: 'host_ended' | 'all_finished' | 'timeout' = 'host_ended'): Promise<{ success: boolean; error?: string }> {
    if (!this.raceRoomId || !this.channelManager) {
      return { success: false, error: 'No room initialized' };
    }

    try {
      const endTime = new Date().toISOString();

      const { error } = await supabase
        .schema('race')
        .from('rooms')
        .update({
          state: 'finished',
          finished_at: endTime,
        })
        .eq('id', this.raceRoomId);

      if (error) {
        return { success: false, error: error.message };
      }

      const payload: EndRacePayload = {
        endTime,
        reason,
      };

      await this.channelManager.broadcast('END_RACE', payload);

      console.log('Race ended:', payload);
      return { success: true };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  }

  async cancelRace(): Promise<{ success: boolean; error?: string }> {
    if (!this.raceRoomId) {
      return { success: false, error: 'No room initialized' };
    }

    try {
      if (this.channelManager) {
        await this.channelManager.broadcast('RACE_CANCELLED', {
          reason: 'host_cancelled',
        });
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const { error } = await supabase
        .schema('race')
        .from('rooms')
        .delete()
        .eq('id', this.raceRoomId);

      if (error) {
        return { success: false, error: error.message };
      }

      console.log('Race cancelled and deleted:', this.raceRoomId);
      return { success: true };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  }

  // ============================================================
  // Participant Management
  // ============================================================

  async getParticipants(): Promise<RaceParticipant[]> {
    if (!this.raceRoomId) return [];

    const { data, error } = await supabase
      .schema('race')
      .from('participants')
      .select('*')
      .eq('race_room_id', this.raceRoomId)
      .neq('status', 'left')
      .order('joined_at', { ascending: true });

    if (error) {
      console.error('Error fetching participants:', error);
      return [];
    }

    return data || [];
  }

  private async refreshParticipants(): Promise<void> {
    const participants = await this.getParticipants();
    this.callbacks.onParticipantsUpdate?.(participants);
  }

  // ============================================================
  // Leaderboard
  // ============================================================

  /**
   * Get leaderboard entries sorted by race criteria.
   * Uses live_stats as the single source of truth for all statistics.
   */
  async getLeaderboard(): Promise<LeaderboardEntry[]> {
    if (!this.raceRoomId) return [];

    // Get participants for metadata (username, status, etc.)
    const participants = await this.getParticipants();

    // Get stats exclusively from live_stats (single source of truth)
    const { data: liveStatsData, error } = await supabase
      .schema('race')
      .from('live_stats')
      .select('user_id, cards_completed, correct_count, incorrect_count, accuracy, total_time')
      .eq('race_room_id', this.raceRoomId);

    if (error) {
      console.error('Error fetching live stats:', error);
    }

    // Map stats by user for efficient lookup
    const statsByUser = new Map<string, LiveStats>();
    for (const row of liveStatsData || []) {
      statsByUser.set(row.user_id, row as LiveStats);
    }

    // Build leaderboard entries
    const entries = participants.map((participant) => {
      // For simulated participants (user_id is null), use participant.id as lookup key
      const lookupKey = participant.user_id || participant.id;
      const stats = statsByUser.get(lookupKey);

      const correctCount = stats?.correct_count || 0;
      const incorrectCount = stats?.incorrect_count || 0;
      const cardsCompleted = stats?.cards_completed || 0;
      const totalTime = stats?.total_time || 0;

      return {
        rank: 0, // Will be assigned after sorting
        participant,
        cardsCompleted,
        correctCount,
        accuracy: calculateAccuracy(correctCount, incorrectCount),
        avgTimePerCard: calculateAvgTimePerCard(totalTime, cardsCompleted),
        totalTime,
        isFinished: participant.status === 'finished',
      };
    });

    // Sort and assign ranks using shared utilities
    const sorted = sortLeaderboard(entries);
    return assignRanks(sorted);
  }

  // ============================================================
  // Getters
  // ============================================================

  getRoom(): RaceRoom | null {
    return this.room;
  }

  getRoomId(): string | null {
    return this.raceRoomId;
  }

  isConnected(): boolean {
    return this.channelManager?.isConnected() ?? false;
  }

  // ============================================================
  // Cleanup
  // ============================================================

  async cleanup(): Promise<void> {
    if (this.channelManager) {
      await this.channelManager.cleanup();
      this.channelManager = null;
    }
    this.raceRoomId = null;
    this.room = null;
  }
}
